"use client";

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  RotateCw,
  IndianRupee,
  Filter,
  X,
} from "lucide-react";
import {
  CategoryWithStats,
  ApiResponse,
  CACHE_DURATION_MINUTES,
  FilterState,
  FilterPanelProps,
} from "../types/types";
import { getCachedData, setCachedData } from "../lib/cacheService";

const INITIAL_FILTERS: FilterState = {
  query: "",
  category: "",
  minPrice: "",
  maxPrice: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  onResetFilters,
  isLoading = false,
}) => {
  const [categories, setCategories] = useState<CategoryWithStats[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const cached = getCachedData<CategoryWithStats[]>(
        "api-cache-categories",
        CACHE_DURATION_MINUTES,
      );
      if (cached) {
        setCategories(cached);
        return;
      }
      try {
        const { data } = await axios.get<ApiResponse<CategoryWithStats[]>>(
          "/api/products/categories",
        );
        if (data.success) {
          setCategories(data.data);
          setCachedData("api-cache-categories", data.data);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      onFilterChange({ ...filters, [name]: value });
    },
    [filters, onFilterChange],
  );

  const handleCategoryClick = useCallback(
    (category: string) => {
      onFilterChange({
        ...filters,
        category: filters.category === category ? "" : category,
      });
    },
    [filters, onFilterChange],
  );

  const removeFilter = (key: keyof FilterState) => {
    onFilterChange({ ...filters, [key]: INITIAL_FILTERS[key] });
  };

  const isFilterActive = React.useMemo(
    () =>
      Object.keys(filters).some(
        (key) =>
          filters[key as keyof FilterState] !==
          INITIAL_FILTERS[key as keyof FilterState],
      ),
    [filters],
  );

  const renderFilters = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-end">
      <div className="md:col-span-2">
        <label
          htmlFor="query"
          className="block text-sm font-bold mb-2 text-stone-300"
        >
          Search by Name
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none text-stone-400" />
          <input
            type="text"
            name="query"
            id="query"
            value={filters.query}
            onChange={handleInputChange}
            placeholder="e.g., Battery Sprayer, SP-300..."
            className="w-full pl-10 pr-10 py-2.5 rounded-md bg-stone-900 border border-stone-700 focus:ring-2 focus:ring-amber-500 text-white"
          />
          {filters.query && (
            <button
              onClick={() => onFilterChange({ ...filters, query: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-stone-600 transition-colors"
              title="Clear search"
            >
              <X size={16} className="text-stone-400" />
            </button>
          )}
        </div>
      </div>
      <div>
        <label className="block text-sm font-bold mb-2 text-stone-300">
          Price Range
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-stone-400" />
            <input
              type="number"
              name="minPrice"
              value={filters.minPrice}
              onChange={handleInputChange}
              placeholder="Min"
              className="w-full pl-8 pr-2 py-2.5 rounded-md bg-stone-900 border border-stone-700 focus:ring-2 focus:ring-amber-500 text-white"
            />
          </div>
          <div className="relative flex-1">
            <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-stone-400" />
            <input
              type="number"
              name="maxPrice"
              value={filters.maxPrice}
              onChange={handleInputChange}
              placeholder="Max"
              className="w-full pl-8 pr-2 py-2.5 rounded-md bg-stone-900 border border-stone-700 focus:ring-2 focus:ring-amber-500 text-white"
            />
          </div>
        </div>
      </div>
      <div>
        <label
          htmlFor="sortBy"
          className="block text-sm font-bold mb-2 text-stone-300"
        >
          Sort By
        </label>
        <div className="flex gap-2">
          <select
            id="sortBy"
            name="sortBy"
            value={filters.sortBy}
            onChange={handleInputChange}
            className="w-2/3 pl-3 py-2.5 rounded-md appearance-none bg-stone-900 border border-stone-700 focus:ring-2 focus:ring-amber-500 text-white"
          >
            <option value="createdAt">Newest</option>
            <option value="updatedAt">Last Updated</option>
            <option value="price">Price</option>
            <option value="modelName">Name</option>
          </select>
          <select
            name="sortOrder"
            value={filters.sortOrder}
            onChange={handleInputChange}
            className="w-1/3 pl-3 py-2.5 rounded-md appearance-none bg-stone-900 border border-stone-700 focus:ring-2 focus:ring-amber-500 text-white"
          >
            <option value="desc">High → Low</option>
            <option value="asc">Low → High</option>
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="mb-12 rounded-xl border border-stone-700/80 bg-black/20 backdrop-blur-sm shadow-2xl"
    >
      {isLoading && (
        <div className="h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent animate-pulse" />
      )}

      <div className="p-4 border-b border-stone-700/80">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-stone-300 mr-2">
            Categories:
          </span>
          {categories.map((c) => (
            <button
              key={c.category}
              onClick={() => handleCategoryClick(c.category)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors duration-200 ${filters.category === c.category ? "bg-amber-400 text-black font-semibold" : "bg-stone-800/60 text-stone-300 hover:bg-stone-700"}`}
            >
              {c.category}
            </button>
          ))}
        </div>
      </div>

      <div className="lg:hidden p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-left font-semibold text-stone-200"
        >
          <div className="flex items-center gap-2">
            <Filter size={18} />
            <span>Advanced Filters</span>
          </div>
          <ChevronDown
            className={`h-5 w-5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Desktop: always visible */}
      <div className="hidden lg:block">
        <div className="p-6 border-t border-stone-800/80 lg:border-t-0">
          {renderFilters()}
        </div>
      </div>

      {/* Mobile: collapsible */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden lg:hidden"
          >
            <div className="p-6 border-t border-stone-800/80">
              {renderFilters()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isFilterActive && (
        <div className="p-4 border-t border-stone-700/80 bg-stone-900/30">
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(filters).map(([key, value]) => {
              if (value === INITIAL_FILTERS[key as keyof FilterState] || !value)
                return null;
              const label =
                key === "query"
                  ? `"${value}"`
                  : key === "minPrice"
                    ? `Min: ₹${value}`
                    : key === "maxPrice"
                      ? `Max: ₹${value}`
                      : value;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="flex items-center gap-1 px-2 py-1 bg-stone-700 text-stone-200 text-xs rounded-full">
                    <span>{label}</span>
                    <button
                      onClick={() => removeFilter(key as keyof FilterState)}
                      className="rounded-full hover:bg-stone-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
            <button
              onClick={onResetFilters}
              className="ml-auto flex items-center gap-1.5 px-2 py-1 text-xs text-stone-400 hover:text-white transition-colors"
            >
              <RotateCw size={14} /> Clear All
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FilterPanel;
